import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { postAppointment } from "@/lib/api";
import { INTEREST_OPTIONS, waLink, WA_MESSAGES } from "@/config/site";

const inputCls =
    "w-full bg-obsidian/60 border border-gold/20 focus:border-gold outline-none px-4 py-3 text-sm text-ivory placeholder:text-sand/50 transition-colors";

const Field = ({ label, children, testId }) => (
    <label className="block" data-testid={testId}>
        <span className="block text-[10px] tracking-[0.25em] uppercase text-sand mb-2">{label}</span>
        {children}
    </label>
);

const AppointmentModal = () => {
    const [open, setOpen] = useState(false);
    const [interest, setInterest] = useState("");
    const [form, setForm] = useState({ name: "", phone: "", email: "", date: "", time: "", message: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            setInterest(e.detail?.interest || "");
            setOpen(true);
        };
        window.addEventListener("bsk:book", handler);
        return () => window.removeEventListener("bsk:book", handler);
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Please share your name and phone number.");
            return;
        }
        setBusy(true);
        try {
            await postAppointment({ ...form, interest });
            toast.success("Appointment requested. We will confirm shortly.");
            setOpen(false);
            setForm({ name: "", phone: "", email: "", date: "", time: "", message: "" });
        } catch {
            toast.error("Something went wrong. Please try WhatsApp instead.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[96] flex items-center justify-center p-4 bg-obsidian/90 backdrop-blur-md"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setOpen(false)}
                    data-testid="appointment-modal"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 32, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 32, scale: 0.98 }}
                        transition={{ duration: 0.4, ease: [0.22, 0.6, 0.24, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-xl bg-charcoal border border-gold/25 p-7 sm:p-10 max-h-[88vh] overflow-y-auto relative"
                    >
                        <button onClick={() => setOpen(false)} data-testid="appointment-close-btn" aria-label="Close" className="absolute top-5 right-5 text-sand hover:text-gold transition-colors">
                            <X size={20} strokeWidth={1.5} />
                        </button>
                        <span className="eyebrow">Private Consultation</span>
                        <h3 className="font-serif text-3xl sm:text-4xl mt-3 leading-tight">Make Your Jewellery Experience Personal</h3>
                        <p className="text-sm text-sand mt-3 leading-relaxed">
                            Visit BSK Jewellers for a personalised conversation about your jewellery requirements.
                        </p>
                        <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Field label="Name *" testId="appointment-name-field">
                                <input required value={form.name} onChange={set("name")} data-testid="appointment-name-input" className={inputCls} placeholder="Your full name" />
                            </Field>
                            <Field label="Phone *" testId="appointment-phone-field">
                                <input required value={form.phone} onChange={set("phone")} data-testid="appointment-phone-input" className={inputCls} placeholder="+91 …" />
                            </Field>
                            <Field label="Email" testId="appointment-email-field">
                                <input type="email" value={form.email} onChange={set("email")} data-testid="appointment-email-input" className={inputCls} placeholder="you@email.com" />
                            </Field>
                            <Field label="Interested In" testId="appointment-interest-field">
                                <select value={interest} onChange={(e) => setInterest(e.target.value)} data-testid="appointment-interest-select" className={inputCls}>
                                    <option value="">Select…</option>
                                    {INTEREST_OPTIONS.map((o) => (
                                        <option key={o} value={o}>{o}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Preferred Date" testId="appointment-date-field">
                                <input type="date" value={form.date} onChange={set("date")} data-testid="appointment-date-input" className={`${inputCls} [color-scheme:dark]`} />
                            </Field>
                            <Field label="Preferred Time" testId="appointment-time-field">
                                <input type="time" value={form.time} onChange={set("time")} data-testid="appointment-time-input" className={`${inputCls} [color-scheme:dark]`} />
                            </Field>
                            <div className="sm:col-span-2">
                                <Field label="Message" testId="appointment-message-field">
                                    <textarea rows={3} value={form.message} onChange={set("message")} data-testid="appointment-message-input" className={inputCls} placeholder="Tell us about the occasion or piece you have in mind…" />
                                </Field>
                            </div>
                            <div className="sm:col-span-2 flex flex-col sm:flex-row gap-4 mt-2">
                                <button
                                    type="submit"
                                    disabled={busy}
                                    data-testid="appointment-submit-btn"
                                    className="flex-1 bg-gold text-obsidian text-xs tracking-[0.25em] uppercase py-4 hover:bg-gold-bright transition-colors disabled:opacity-60"
                                >
                                    {busy ? "Sending…" : "Request Appointment"}
                                </button>
                                <a
                                    href={waLink(WA_MESSAGES.appointment)}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="appointment-whatsapp-btn"
                                    className="flex-1 border border-gold/50 text-gold text-xs tracking-[0.25em] uppercase py-4 flex items-center justify-center gap-2 hover:bg-gold/10 transition-colors"
                                >
                                    <MessageCircle size={15} /> Confirm on WhatsApp
                                </a>
                            </div>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default AppointmentModal;
